import React, { useEffect, useRef, useState } from 'react';
import { BASIC_NOTICE_TEMPLATE, sanitizeNoticeHtml, toEditorHtml } from './noticeHtml';

type NoticeRichTextEditorProps = {
  initialContent: string;
  onChange: (html: string) => void;
  onUploadImage: (file: File) => Promise<string>;
};

const NoticeRichTextEditor = ({
  initialContent,
  onChange,
  onUploadImage,
}: NoticeRichTextEditorProps) => {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const savedRange = useRef<Range | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!surfaceRef.current) return;
    surfaceRef.current.innerHTML = toEditorHtml(initialContent);
    // 공지를 바꿀 때는 부모가 key로 에디터를 다시 마운트한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    const html = surfaceRef.current?.innerHTML ?? '';
    onChange(sanitizeNoticeHtml(html));
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !surfaceRef.current) return;
    const range = selection.getRangeAt(0);
    if (surfaceRef.current.contains(range.commonAncestorContainer)) {
      savedRange.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const range = savedRange.current;
    const selection = window.getSelection();
    if (!range || !selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const exec = (command: string, value?: string) => {
    surfaceRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, value);
    saveSelection();
    emit();
  };

  const insertLink = () => {
    const url = window.prompt('링크 주소 (https://)', 'https://');
    if (!url) return;
    if (!/^https?:\/\/\S+$/i.test(url.trim())) {
      window.alert('http 또는 https 주소만 넣을 수 있습니다.');
      return;
    }
    exec('createLink', url.trim());
  };

  const insertTemplate = () => {
    exec('insertHTML', BASIC_NOTICE_TEMPLATE);
  };

  const onPickImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 1024 * 1024) {
      window.alert('이미지는 1MB 이하만 등록할 수 있습니다.');
      return;
    }
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      exec('insertImage', url);
    } catch (error) {
      const message = error instanceof Error ? error.message : '이미지 등록에 실패했습니다.';
      window.alert(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="notice_editor">
      <div className="editor_toolbar" role="toolbar" aria-label="공지 서식">
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => exec('bold')}
        >
          굵게
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => exec('italic')}
        >
          기울임
        </button>
        <label className="color_label">
          색
          <input
            type="color"
            aria-label="글자 색"
            defaultValue="#243024"
            onMouseDown={saveSelection}
            onChange={(event) => exec('foreColor', event.target.value)}
          />
        </label>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => exec('insertUnorderedList')}
        >
          목록
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => exec('insertOrderedList')}
        >
          번호
        </button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={insertLink}>
          링크
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? '등록중' : '이미지'}
        </button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={insertTemplate}
        >
          기본 서식
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          hidden
          onChange={onPickImage}
        />
      </div>
      <div
        ref={surfaceRef}
        className="editor_surface"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="공지사항 내용"
        onInput={emit}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
      />
    </div>
  );
};

export default NoticeRichTextEditor;
